/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { StateCreator } from 'zustand';

import { User } from '@/client';

export type SubtenantRole = 'none' | 'viewer' | 'editor' | 'admin';

export type SelectedSubtenant = {
  id: string;
  name: string;
  parentId: string;
  role?: SubtenantRole;
};

export type SubtenantSwitcherSlice = {
  selectedSubtenantID?: string;
  selectedSubtenantParentID?: string;
  subtenantRole?: SubtenantRole;
  setSelectedSubtenantID: (subtenantID?: string) => void;
  setSelectedSubtenantParentID: (parentID?: string) => void;
  setSubtenantRole: (role?: SubtenantRole) => void;
  setSelectedSubtenant: (subtenant: SelectedSubtenant | undefined) => void;
  clearSubtenantState: () => void;
  getCurrentEffectiveRole: (userProfile?: User) => User['role'] | undefined;
};

export const selectedSubtenantIDKey = 'selectedSubtenantID';

const selectedSubtenantID = localStorage.getItem(selectedSubtenantIDKey);

export const createSubtenantSwitcherSlice: StateCreator<SubtenantSwitcherSlice> = (set, get) => ({
  selectedSubtenantID: selectedSubtenantID || undefined,
  selectedSubtenantParentID: undefined,
  subtenantRole: undefined,
  setSelectedSubtenantID: (subtenantID) =>
    set((state) => {
      if (subtenantID) {
        localStorage.setItem(selectedSubtenantIDKey, subtenantID);
      } else {
        localStorage.removeItem(selectedSubtenantIDKey);
      }
      return { ...state, selectedSubtenantID: subtenantID };
    }),
  setSelectedSubtenantParentID: (parentID) => set((state) => ({ ...state, selectedSubtenantParentID: parentID })),
  setSubtenantRole: (role) => set((state) => ({ ...state, subtenantRole: role })),
  setSelectedSubtenant: (subtenant) =>
    set((state) => {
      if (subtenant) {
        localStorage.setItem(selectedSubtenantIDKey, subtenant.id);
        return {
          ...state,
          selectedSubtenantID: subtenant.id,
          selectedSubtenantParentID: subtenant.parentId,
          subtenantRole: subtenant.role,
        };
      }
      localStorage.removeItem(selectedSubtenantIDKey);
      return {
        ...state,
        selectedSubtenantID: undefined,
        selectedSubtenantParentID: undefined,
        subtenantRole: undefined,
      };
    }),
  clearSubtenantState: () =>
    set((state) => {
      localStorage.removeItem(selectedSubtenantIDKey);
      return {
        ...state,
        selectedSubtenantID: undefined,
        selectedSubtenantParentID: undefined,
        subtenantRole: undefined,
      };
    }),
  getCurrentEffectiveRole: (userProfile) => {
    const state = get();
    // If subtenant is selected and has a role, map and return subtenant role
    if (state.selectedSubtenantID && state.subtenantRole) {
      // Explicitly map subtenant roles to user roles
      const subtenantToUserRoleMap: Record<'viewer' | 'editor' | 'admin', User['role']> = {
        viewer: 'viewer',
        editor: 'editor',
        admin: 'admin',
      };
      return subtenantToUserRoleMap[state.subtenantRole as 'viewer' | 'editor' | 'admin'];
    }
    // Otherwise return user's tenant-level role
    return userProfile?.role;
  },
});
